import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { APIRequestStatus } from 'common-ts/dist/models/APIRequestStatus';
import { ProductOnUser, Collection } from 'common-ts/dist/models/Product';
import { ProductsList } from 'common-ts/dist/models/Product';
import axios from 'axios';
import { RootState } from '../store';
import { Mixpanel } from '../mixpanel';
import { BodyMeasurementsForSize } from 'common-ts/dist/models/UserMeasurements';
import { RegularSizeValue } from 'common-ts/dist/models/Cart';
import { setProductSpotlightList } from './CartRedux';
import { changeFileDataSource } from '../App';

// Define the initial state using that type
const initialState: ProductsList = {
  collectionsDictionary: {},
  productsDictionary: {},

  discoveryCollectionsOrder: [],
  efficientCollectionsOrder: [],
  efficientProductsOrder: [],
  discoveryCollectionsOrderDict: {},

  productViewEventTimerDict: {},

  productFamilyMediaHasViewedDict: {},

  status: APIRequestStatus.Idle,
  error: null,

  productSizeAvailabilityDict: {},

  statusSizeAvailability: APIRequestStatus.Idle,
}

const incrementPricePercent = 4;
const getPriceAfterAdjustment = (beforePrice: number, productId: string) => {

  // Disable this per March 30
  return beforePrice;

  // IF THE PRODUCT IS NEW, WE MAINTAIN LIMIT PRICE
  if (productId?.substring(0, 7) >= '2025-10') {
    return beforePrice;
  }
  // MAINTAIN NULL PRICING
  if (beforePrice === -1) {
    return -1;
  }

  const draftIncreasePrice = beforePrice * incrementPricePercent / 100;
  const roundedDraftIncreasePrice = Math.ceil(draftIncreasePrice / 500) * 500; // Rounda to nearest ceiling 500

  let afterPrice = beforePrice + roundedDraftIncreasePrice;

  // MAINTAIN PRICE BRACKETING
  if (beforePrice < 300000 && afterPrice >= 300000) {
    afterPrice = beforePrice > 298500 ? beforePrice : 298500;
  } else if (beforePrice < 200000 && afterPrice >= 200000) {
    afterPrice = beforePrice > 198000 ? beforePrice : 198000;
  }

  return afterPrice;
};

// productUpdatePost endpoint (also hosts the offline-branch price override read).
const PRODUCT_UPDATE_POST_URL =
  'https://i3fxe6nvj7.execute-api.ap-southeast-1.amazonaws.com/prod/product/update';

type BranchPriceOverride = {
  originalPrice?: number;
  discountedPrice?: number;
  extraSizeChargePrice?: number;
};

// Last-fetched offline-branch overrides, kept module-side so a products refresh can re-apply
// them without widening the shared ProductsList type.
let cachedBranchOverrides: Record<string, BranchPriceOverride> = {};

// The offline store device is pinned to a branch via localStorage (set once). Only honor it
// in the offline-store context — an internal iframe or the /offline-measurements route — so a
// regular customer's phone/browser never picks up branch pricing (uses the base dictionary).
export const getOfflineBranchId = (): string | null => {
  try {
    const isInsideIframe = window.self !== window.top;
    const isOfflineRoute = window.location.href.includes('offline-measurements');
    if (!isInsideIframe && !isOfflineRoute) return null;
    return localStorage.getItem('offlineBranchId');
  } catch {
    return null;
  }
};

// Offline "true stock" toggle (set once via ?setTrueStock=1): availability is then
// requested and order-checked on provisioned - hutang stok, so offline buyers are only
// offered stock that can ship in days. Same context gate as getOfflineBranchId.
export const getOfflineTrueStock = (): boolean => {
  try {
    const isInsideIframe = window.self !== window.top;
    const isOfflineRoute = window.location.href.includes('offline-measurements');
    if (!isInsideIframe && !isOfflineRoute) return false;
    return localStorage.getItem('offlineTrueStock') === '1';
  } catch {
    return false;
  }
};

// Overlay branch price overrides onto the loaded products (mutates Immer draft in place).
function applyBranchOverridesToDict(
  productsDictionary: any,
  overrides: Record<string, BranchPriceOverride>
) {
  for (const productId of Object.keys(overrides || {})) {
    const product = productsDictionary[productId];
    if (!product) continue;
    const ov = overrides[productId];
    if (ov.originalPrice !== undefined) product.originalPrice = ov.originalPrice;
    if (ov.discountedPrice !== undefined) product.discountedPrice = ov.discountedPrice;
    if (ov.extraSizeChargePrice !== undefined)
      product.extraSizeChargePrice = ov.extraSizeChargePrice;
  }
}

export const fetchBranchPriceOverrides = createAsyncThunk(
  'productsList/fetchBranchPriceOverrides',
  async () => {
    const branchId = getOfflineBranchId();
    if (!branchId) return {} as Record<string, BranchPriceOverride>;
    const response = await axios({
      method: 'post',
      url: PRODUCT_UPDATE_POST_URL,
      data: JSON.stringify({ action: 'getBranchPriceOverrides', branchId })
    });
    return (response.data?.overrides ?? {}) as Record<string, BranchPriceOverride>;
  }
);

export const fetchProductsList = createAsyncThunk('productsList/fetchProductsList', async (payload: any, thunkAPI) => {
  const response = await axios({
    method: 'get',
    url: 'https://studiosclo.uk/products-cache',
  });

  // Re-fetch reviews
  thunkAPI.dispatch(fetchProductsSizeAvailabilityDict(response.data.productIdList));

  // Offline branches: refresh price overrides so they re-apply on top of the new list.
  if (getOfflineBranchId()) {
    thunkAPI.dispatch(fetchBranchPriceOverrides());
  }

  // if (response.data.productFlashSaleList) {
  //   thunkAPI.dispatch(setProductSpotlightList(response.data.productFlashSaleList));
  // }

  return response.data
})

export const fetchProductsSizeAvailabilityDict = createAsyncThunk('productsList/fetchProductsSizeAvailabilityDict', async (payload: string[], thunkAPI) => {

  var measurementsArray = [];
  measurementsArray.push(BodyMeasurementsForSize[RegularSizeValue.XXS]);
  measurementsArray.push(BodyMeasurementsForSize[RegularSizeValue.XS]);
  measurementsArray.push(BodyMeasurementsForSize[RegularSizeValue.S]);
  measurementsArray.push(BodyMeasurementsForSize[RegularSizeValue.M]);
  measurementsArray.push(BodyMeasurementsForSize[RegularSizeValue.L]);
  measurementsArray.push(BodyMeasurementsForSize[RegularSizeValue.XL]);
  measurementsArray.push(BodyMeasurementsForSize[RegularSizeValue.XXL]);

  const response = await axios({
    method: 'post',
    url: 'https://i3fxe6nvj7.execute-api.ap-southeast-1.amazonaws.com/prod/availability/product-size',
    data: JSON.stringify({
      productIdArray: payload,
      measurementsArray: measurementsArray,
      ...(getOfflineTrueStock() ? { trueStock: true } : {})
    })
  });

  if (response.data.productFlashSaleList) {
    thunkAPI.dispatch(setProductSpotlightList(response.data.productFlashSaleList));
  }

  return JSON.parse(response.data.sizeAvailability);
})

// export const attemptProductGlimpseEvent = createAsyncThunk('productsList/attemptProductGlimpseEvent', 
// async (payload: string, thunkAPI) => {
//   let productViewEventTimerDict = (thunkAPI.getState() as RootState).productsList.productViewEventTimerDict;

//   var currentTimestamp = Math.floor(Date.now() / 1000); // Timestamp in seconds

//   // We send another event if more than 10 seconds have passed since the last glimpse event
//   if (currentTimestamp - productViewEventTimerDict[payload] > 10) {
//     productViewEventTimerDict[payload] = currentTimestamp;
    
//   }

//   return response.data
// })

export const productSlice = createSlice({
  name: 'productsList',
  initialState,
  reducers: {
    attemptProductGlimpseEvent: (state, action: PayloadAction<string>) => {
      var currentTimestamp = Math.floor(Date.now() / 1000); // Timestamp in seconds

      // We send another event if more than 10 seconds have passed since the last glimpse event.
      // Each "impression" should have a 10 second interval from one another,
      // and is counted as one view / impression.
      // whereas one "reach" follows the one reach per user rule.
      if (state.productViewEventTimerDict[action.payload] === undefined
        || currentTimestamp - state.productViewEventTimerDict[action.payload] > 10) {
        state.productViewEventTimerDict[action.payload] = currentTimestamp;

        Mixpanel.track('productGlimpse', {
          'productId': "productId-" + action.payload,
          'timestamp': currentTimestamp
        });
        console.log("Tracking mixpanel for productId " + action.payload + " and timestamp " + currentTimestamp);
      }
    },
    updateProductFamilyMediaHasViewedDict: (state, action: PayloadAction<string[]>) => {
      for (let familyMediaUrl of action.payload) {
        state.productFamilyMediaHasViewedDict[familyMediaUrl] = true;
      }
    }
  },
  extraReducers(builder) {
    builder
      .addCase(fetchProductsList.pending, (state, action) => {
        state.status = APIRequestStatus.RequestInProgress;
      })
      .addCase(fetchProductsList.fulfilled, (state, action) => {
        // Payload is already in Object format.

        // Product
        const productDetailArray: ProductOnUser[] = JSON.parse(changeFileDataSource(JSON.stringify(action.payload.products)));
        state.productsDictionary = {};
        for (let key in productDetailArray) {
          const productDetail: ProductOnUser = productDetailArray[key];
          state.productsDictionary[productDetail.productId] = {
            ...productDetail,
            originalPrice: productDetail.bundlingData ? productDetail.bundlingData.bundlingPrice1 :
            getPriceAfterAdjustment(productDetail.originalPrice, productDetail.productId),
            discountedPrice: productDetail.bundlingData ? -1 :
            getPriceAfterAdjustment(productDetail.discountedPrice, productDetail.productId),
          };
        }

        // Re-apply any offline-branch price overrides on top of the freshly loaded list.
        applyBranchOverridesToDict(state.productsDictionary, cachedBranchOverrides);

        // Collection
        const collectionArray: Collection[] = JSON.parse(changeFileDataSource(JSON.stringify(action.payload.collections)));
        state.collectionsDictionary = {};
        for (let key in collectionArray) {
          const collectionDetail: Collection = collectionArray[key];
          state.collectionsDictionary[collectionDetail.collectionId] = collectionDetail;
        }

        state.discoveryCollectionsOrder = action.payload.discoveryCollectionsOrder;
        state.discoveryCollectionsOrderDict = action.payload.discoveryCollectionsOrderDict ?? {};
        state.efficientCollectionsOrder = action.payload.efficientCollectionsOrder;
        state.efficientProductsOrder = action.payload.efficientProductsOrder;

        state.status = APIRequestStatus.Success;
      })
      .addCase(fetchProductsList.rejected, (state, action) => {
        state.status = APIRequestStatus.Failure;
        // state.error = action.error.message
        console.log("fetch is failure");
      })
      .addCase(fetchBranchPriceOverrides.fulfilled, (state, action) => {
        cachedBranchOverrides = action.payload;
        applyBranchOverridesToDict(state.productsDictionary, cachedBranchOverrides);
      })
      .addCase(fetchProductsSizeAvailabilityDict.pending, (state, action) => {
        state.statusSizeAvailability = APIRequestStatus.RequestInProgress;
      })
      .addCase(fetchProductsSizeAvailabilityDict.fulfilled, (state, action) => {
        // Payload is already in Object format.

        // Product Size Availability Dict
        state.productSizeAvailabilityDict = action.payload;

        state.statusSizeAvailability = APIRequestStatus.Success;
      })
      .addCase(fetchProductsSizeAvailabilityDict.rejected, (state, action) => {
        state.statusSizeAvailability = APIRequestStatus.Failure;
      })
  }
})

// Action creators are generated for each case reducer function
export const { attemptProductGlimpseEvent, updateProductFamilyMediaHasViewedDict } = productSlice.actions

export default productSlice.reducer